import type { AppointmentTextOption } from './functions/appointmentText';

export type SchedulerMode = 'team' | 'projects';
export type ViewType = 'personal_calendar' | 'employee_list' | 'employee_timeline' | 'project_planner';
export type Direction = 'stacked' | 'columns';
export type Period = 'day' | 'week';
export type ShowDays = 'mon-fri' | 'mon-sat' | 'all';
export type ColorMode = "status" | "custom" | "type";
export type TimeDisplayMode = "none" | "timeslots" | "timeblocks";

export type SchedulingView = {
    mode: SchedulerMode; // The mode of the scheduler, either 'team' or 'projects'
    viewType: ViewType; // The type of view of the scheduler. For example: 'resource_list', 'resource_timeline' or 'project_planner'
    direction: Direction; // The direction of the scheduler: 'stacked' or 'columns'.
    period: Period; // Sets the period which is rendered. For example: 'day' or 'week'
    showDays: ShowDays; // The days to display in the scheduler. For example: 'mon-fri', 'mon-sat' or 'all'
    showTimes?: { startTime: string, endTime: string } | null; 
    timeDisplayMode: TimeDisplayMode;
    renderTimeBlocks: boolean;
    colorMode: ColorMode;
    displayDate: Date; // The date on which the scheduling is shown
    showProfilePictures: boolean;
    showUnassignedResource: boolean;
    showJobInbox: boolean;
    showSchedulerMap: boolean;
    createJobs: boolean;
    appointmentText: AppointmentTextOption[]; // The job fields shown in the appointment blocks, in order
}

export type ResourceType = {
    id: number | string | null;
    uuid?: string;
    resource_group: number | string;
    name: string;
    user_hash: string | null;
    user_created: string | null;
    image: string | null;
    remark: string | null;
    ordering: number;
    deleted: boolean;
    deleted_at: string | null;
    division: number | null;
    pair_id?: number | null; // The id of the worker pair of the resource user
    pair_role?: 'foreman' | 'helper' | null; // The role of the resource user within its worker pair
    pair_partner_user_hash?: string | null; // The user hash of the worker pair partner
    pair_partner_name?: string | null; // The name of the worker pair partner
    is_helper_role?: boolean; // Whether the resource user has the fixed helper role
    is_subcontractor_role?: boolean; // Whether the resource user has the fixed subcontractor role
    company_name?: string | null; // The company name of a subcontractor resource user
    resource_type?: 'user' | 'equipment'; // Whether the row is an employee row or an equipment row
    equipment_id?: number | null; // The id of the equipment of an equipment row
    equipment_name?: string | null; // The name of the equipment of an equipment row
    equipment_type_name?: string | null; // The type name of the equipment of an equipment row
    equipment_number?: string | null; // The number of the equipment of an equipment row
    equipment_deactivate?: boolean; // Set when a deleted row in the equipment group must deactivate the equipment
};

export const createNewResource = (resource_group: number | string, uuid: string, ordering: number): ResourceType => ({
    id: null,
    uuid: uuid,
    resource_group: resource_group,
    name: '',
    user_hash: null,
    user_created: null,
    image: null,
    remark: null,
    ordering: ordering,
    deleted: false,
    deleted_at: null,
    division: null,
    pair_id: null,
    pair_role: null,
    pair_partner_user_hash: null,
    pair_partner_name: null,
    is_helper_role: false,
    resource_type: 'user',
    equipment_id: null,
    equipment_name: null,
    equipment_type_name: null,
    equipment_number: null,
});

export type ResourceGroupType = {
    id: number | string | null;
    uuid?: string;
    scheduling_board: number;
    name: string;
    ordering: number;
    is_equipment_group?: boolean; // The fixed equipment group at the bottom of the board
    deleted: boolean;
    deleted_at: string | null;
    division: number | null;
    resources: ResourceType[];
};

export const createNewResourceGroup = (scheduling_board: number, uuid: string, ordering: number, isEquipmentGroup: boolean = false): ResourceGroupType => ({
    id: null,
    uuid: uuid,
    scheduling_board: scheduling_board,
    name: '',
    ordering: ordering,
    is_equipment_group: isEquipmentGroup,
    resources: [],
    deleted: false,
    deleted_at: null,
    division: null,
});

// Equipment as returned by the equipment list endpoint
export type EquipmentType = {
    id: number;
    name: string;
    type: number | null;
    type_name: string | null;
    number: string | null;
    remark: string | null;
    is_active: boolean;
};

export type SchedulingBoardType = {
    id: number;
    name: string;
    ordering: number;
    is_active: boolean;
    division: number | null;
    resource_groups: ResourceGroupType[];
}

export type GeocodeStatus = 'not_attempted' | 'success' | 'failed' | 'skipped_limit_reached';

export interface SchedulingJobType {
    id: number;
    client_name?: string;
    type_name?: string;
    type_color?: string | null;
    work_location_street?: string;
    work_location_house_number?: string;
    work_location_postal_code?: string;
    work_location_city?: string;
    work_location_latitude?: string | null;
    work_location_longitude?: string | null;
    work_location_geocode_status?: GeocodeStatus;
    internal_reference?: string | null;
    external_reference?: string | null;
    number?: string | null;
    status_condition?: string | null;
    status_name?: string | null;
    resolution?: string | null;
    main_description?: string | null;
    project_number?: string | null; // Only fetched when the appointment text shows a project field
    project_name?: string | null; // Only fetched when the appointment text shows a project field
    target_date?: string | null;
    estimated_duration?: string | null;
    labels?: {
        name: string;
        color: string;
    }[] | null;
    deleted?: boolean;
    division_id?: number | null;
    expected_delivery_date?: string | null;
}

// Appointment scheduled within the displayed map date range
export interface MapAppointmentType {
    id: number;
    start_datetime: string;
    end_datetime: string;
    status: string;
    timezone?: string | null;
    job_id: number;
    job_client_name?: string | null;
    job_type_name?: string | null;
    job_number?: string | null;
    job_main_description?: string | null;
    job_status_condition?: string | null;
    job_status_name?: string | null;
    job_labels?: { name: string; color: string | null }[];
    work_location_id: number | null;
    work_location_latitude: string | null;
    work_location_longitude: string | null;
    work_location_geocode_status: GeocodeStatus;
    work_location_street?: string | null;
    work_location_house_number?: string | null;
    work_location_postal_code?: string | null;
    work_location_city?: string | null;
    assignees: { id: number; name: string | null }[];
}

// Not yet planned job shown on the map, filtered by status and target date
export interface MapToPlanJobType {
    id: number;
    client_name?: string | null;
    type_name?: string | null;
    status_condition?: string | null;
    status_name?: string | null;
    number?: string | null;
    main_description?: string | null;
    target_date?: string | null;
    estimated_duration?: string | null;
    division_id?: number | null;
    work_location_id: number | null;
    work_location_latitude: string | null;
    work_location_longitude: string | null;
    work_location_geocode_status: GeocodeStatus;
    work_location_street?: string | null;
    work_location_house_number?: string | null;
    work_location_postal_code?: string | null;
    work_location_city?: string | null;
}

// Employee status shown by the gps marker icon on the map
export type MapEmployeeStatus = "traveling" | "working" | "unknown";

// Current employee position shown on the map, sourced from the mobile app gps updates
export interface MapEmployeeType {
    id: number;
    user_id: number;
    user_hash: string | null;
    name: string | null;
    image: string | null;
    latitude: string | null;
    longitude: string | null;
    status: MapEmployeeStatus;
    last_gps_at: string;
    job_client_name?: string | null;
    job_street?: string | null;
    job_house_number?: string | null;
    job_city?: string | null;
}

export interface SchedulerMapDataType {
    appointments: MapAppointmentType[];
    to_plan_jobs: MapToPlanJobType[];
    to_plan_jobs_hidden_count: number;
    to_plan_target_date_window_weeks: number;
    to_plan_jobs_without_target_date_limit: number;
    has_manual_to_schedule_status: boolean;
    geocoding_pending: boolean;
    employees: MapEmployeeType[];
    server_time: string;
}

export interface BaseEventType {
    type: "appointment" | "timerow" | "activity" | "availableEmployee" | "projectJobEmployeeEvent" | "individualJobEmployeeEvent";
    key: string;
    resourceId: number | string;
    startDateTime?: string;
    endDateTime?: string;
    dayKey: string;
    timeSlot?: string;
    startTime?: string;
    endTime?: string;
    timezone?: string;
    durationMinutes?: number;
    deleted: boolean;
}


// Employee scheduler event types
export interface AppointmentEventType extends BaseEventType {
    type: "appointment";
    appointmentId: number | null;
    appointmentUuid?: string;
    status: string;
    custom_color: string | null;
    locked: boolean;
    running_timer?: string | null;
    fallback_status_condition?: string | null;
    job: SchedulingJobType;
}

export interface TimeRowEventType extends BaseEventType {
    type: "timerow";
    timerowId: number;
    appointmentId: number | null;
    fallback_status_condition?: string | null;
    job: SchedulingJobType;
}

export interface ActivityEventType extends BaseEventType {
    type: "activity";
    activityId: number | null; // Null for a virtual occurrence of an activity recurrence
    recurrenceId?: number; // Set on a virtual occurrence, identifies the recurrence it was expanded from
    occurrenceDate?: string; // Set on a virtual occurrence, the date this occurrence falls on
    time_type_name: string | null;
    note: string | null;
    custom_color: string | null;
}


// Project scheduler event types
export interface AvailableEmployeeEvent extends BaseEventType {
    type: "availableEmployee";
    employeeId: number;
    employeeName: string;
}

export interface ProjectJobEmployeeEvent extends BaseEventType {
    type: "projectJobEmployeeEvent";
    appointmentId: number | null;
    appointmentUuid?: string;
    assigneeId: number;
    assigneeName: string;
    status: string;
}

export interface IndividualJobEmployeeEvent extends BaseEventType {
    type: "individualJobEmployeeEvent";
    stackIndex?: number;
    appointmentId: number | null;
    appointmentUuid?: string;
    assigneeId: number;
    assigneeName: string;
    status: string;
}

export type EmployeeSchedulerEventType = AppointmentEventType | TimeRowEventType | ActivityEventType;
export type ProjectSchedulerEventType = AvailableEmployeeEvent | ProjectJobEmployeeEvent | IndividualJobEmployeeEvent;
export type EventType = ProjectSchedulerEventType | EmployeeSchedulerEventType;

export interface EventsListType {
    [eventKey: string]: EventType;
}

export interface EventsByResourceType {
    [resourceId: string]: string[];
}

export interface EmployeeAvailabilityDayType {
    is_working: boolean;
    start_time: string | null;
    end_time: string | null;
}

export interface EmployeeAvailabilityType {
    [resourceId: string]: {
        [isoDate: string]: EmployeeAvailabilityDayType;
    };
}

export type AvailabilityByDayType = Record<string, Record<number, EventType[]>>;

export interface EventTooltipType {
    visible: boolean;
    x: number;
    y: number;
    event: EventType | null;
}

export interface TimeBlock {
    name: string;
    start_time: string;
    end_time: string;
    division: number;
}

export type SchedulerDraggableId =
    // Employee scheduler
    | { 
        type: 'jobInbox'; 
        job: SchedulingJobType;
        weekKey?: string;
    } 
    | { 
        type: 'appointment'; 
        event: AppointmentEventType, 
    }
    | { 
        type: 'activity'; 
        event: ActivityEventType, 
    }
    // Project scheduler
    | { 
        type: 'availableEmployee'; 
        event: AvailableEmployeeEvent, 
    }
    | { 
        type: 'projectJobEmployeeEvent'; 
        event: ProjectJobEmployeeEvent, 
    }
    | { 
        type: 'individualJobEmployeeEvent'; 
        event: IndividualJobEmployeeEvent, 
    };

export type SchedulerDroppableId =
    | { 
        type: 'timeSlot'; 
        resourceId: string | number; 
        day: string; 
        timeSlot: string;
    }
    | { 
        type: 'timeBlock'; 
        resourceId: string | number; 
        day: string; 
        timeBlock: string;
    }
    | { 
        type: 'dayList'; 
        resourceId: string | number; 
        day: string; 
    }
    | { 
        type: 'jobInbox';
        section: 'pastJobs' | 'untargetedJobs' | 'weekJobs';
        weekKey?: string;
    }
    | { 
        type: 'availableEmployeesRow'; 
        resourceId: 'available_employees';
        day: string; 
    }
    | { 
        type: 'projectJob'; 
        resourceId: string | number; 
        project_number: string;
        default_start_time: string | null;
        default_end_time: string | null;
        default_break_duration: string | null;
        day: string; 
    }
    | { 
        type: 'individualJob'; 
        resourceId: string | number;
        job_number: string;
        day: string; 
        timeSlot: string;
    };

export type CalendarDraggingPlaceholder = {
    day: string, 
    timeSlot?: string, 
    durationMinutes?: number
}

export type SchedulerDraggingPlaceholder = {
    resourceId: number | string, 
    day: string, 
    timeSlot?: string, 
    durationMinutes?: number
}

export interface SchedulerDragEventDetails {
    sourceType?: string;
    sourceSection?: string;
    sourceWeekKey?: string;
    destinationSection?: string;
    destinationWeekKey?: string;
    draggableItemType?: string;
    draggableItemId?: number;
    draggableJob?: SchedulingJobType;
}